// Sincronizador de la cola offline ejecutado en el contexto del CLIENTE.
//
// ¿Por qué un cliente y no solo el SW?
// - El Service Worker, en algunos navegadores y condiciones (iOS Safari sin
//   sync API, modos restringidos, errores de fetch sin error visible), no
//   procesa la cola con la fiabilidad necesaria. Los uploads de fotos en
//   particular han demostrado fallar silenciosamente.
// - Cuando la app está abierta y la red vuelve, el camino más fiable es
//   ejecutar la subida con el MISMO axios que usa el flujo online — así el
//   código de éxito está demostrado y los errores son visibles.
// - El SW sigue como respaldo para background y app cerrada.
//
// Replica al pie de la letra el flujo de EditDetailPage.handleSubmit y el
// uploadImagesOneByOne de la app Android (MainRepository.kt).
import {
  createDetail,
  editDetail,
  deleteDetail,
  uploadImages,
  getImages,
  getDetailsByCargo,
} from './api';
import {
  getPendingDetails,
  deletePendingDetail,
  updatePendingDetailToEdit,
  getPendingById,
  putPendingItem,
  resetAllPendingRetries,
  updateCachedCargoDetails,
  acquirePendingLease,
  renewPendingLease,
  releasePendingLease,
  MAX_RETRIES, findCachedPositionByCargo } from './db';
import type { PendingDetail } from './db';
import type { ApiImage, Detail } from '../types';
import { track } from './analytics';
import { telemetryLog, telemetryStatus } from './telemetry';
import { esCancelacionPropia } from '../utils/errors';
import { isOfflineForUser, isUserOfflineMode, describeCoverage } from './networkStatus';
import { ensureEditTs } from '../utils/metadata';
import { addDiscardNotice } from './discardNotices';
import { captureSyncError, describeHttpResponse } from './errorTracking';
import { invalidateSwApiCache } from './swCache';

interface SyncContext {
  signal?: AbortSignal;
}

export interface FlushStats {
  synced: number;
  failed: number;
  processedAny: boolean;
}

let inFlight: Promise<FlushStats | null> | null = null;

// Identidad de ESTE contexto para el lease cross-cola (el SW usa la suya).
// Aleatoria por sesión: si la pestaña muere a mitad, sus leases caducan solos
// y el SW puede robarlos.
const LEASE_OWNER = 'client-' + Math.random().toString(36).slice(2, 10);

// Auto-reset de reintentos: cada 30 min, si hay items que agotaron su tope de
// reintentos, se les da una tanda nueva automáticamente. Equivale a pulsar
// "forzar subida" sin intervención humana (espejo del auto-reset de Android).
const AUTO_RETRY_RESET_MS = 30 * 60 * 1000;
const RETRY_RESET_KEY = 'QUEUE_LAST_RETRY_RESET';

// ── Descarte controlado de fotos irreconstruibles (patrón Android) ──────────
// Una foto cuyo blob no se puede reconstruir NUNCA se descarta a la ligera:
// hacen falta ≥5 comprobaciones de ausencia en servidor —cada una con 200 REAL
// de GetImagesToAppController; un fallo de red no cuenta— repartidas en ≥48h
// desde el primer fallo. El throttle de 6h espacia las comprobaciones (el poll
// pasa cada ~1,5s con cola activa). Si la foto aparece en servidor durante el
// ciclo → limpieza sin pérdida; si el blob se reconstruye → contador a cero
// (autocuración).
const UNRECOVERABLE_MIN_CHECKS = 5;
const UNRECOVERABLE_MIN_AGE_MS = 48 * 60 * 60 * 1000;
const UNRECOVERABLE_CHECK_GAP_MS = 6 * 60 * 60 * 1000;

// ── Modo supervivencia (patrón Android) ─────────────────────────────────────
// A partir de N reintentos REALES del item (los fallos sin red no cuentan),
// las fotos se recomprimen agresivamente SOLO para el envío: el fileName no
// cambia (dedup intacta) y el blob original en IDB no se toca — si la red
// mejora, nada se ha degradado de forma permanente.
const SURVIVAL_RETRIES = 5;
const SURVIVAL_MAX_DIM = 1024;
const SURVIVAL_QUALITY = 0.65;

// ── Back-off de 404 en EDIT ─────────────────────────────────────────────────
// Un detalle borrado en el portal responde 404 a cada EDIT y el item entraba
// en bucle frío continuo. Con ≥3 404s consecutivos, un intento cada 15 min
// como máximo. (El cierre definitivo lo hace confirmDetailGone; esto es la red
// de seguridad para cuando la confirmación no se puede obtener.)
const NOT_FOUND_BACKOFF_AFTER = 3;
const NOT_FOUND_BACKOFF_MS = 15 * 60 * 1000;

// ── Backoff exponencial por item (lección de campo) ─────────────────────────
// El poll busy (~1,5s) contra un fallo instantáneo (ERR_NETWORK en 3ms)
// quemaba el tope de reintentos en menos de un minuto. Tras cada fallo el item
// no vuelve a intentarse hasta now + min(5s × 2^retries, 5min) — las pasadas
// lo saltan sin consumir retry ni lease. Así el tope (100) vuelve a significar
// horas de fallos REALES, como en Android. Solo afecta a items con fallos: en
// buena red nadie tiene nextAttemptAt.
const RETRY_BACKOFF_BASE_MS = 5_000;
const RETRY_BACKOFF_MAX_MS = 5 * 60 * 1000;

function backoffDelayMs(retries: number): number {
  // Cap del exponente para no desbordar; a partir de ~6 ya supera el máximo.
  return Math.min(RETRY_BACKOFF_BASE_MS * 2 ** Math.min(retries, 10), RETRY_BACKOFF_MAX_MS);
}

// ── Status de la cola en el visor: PUERTA ÚNICA de escritura ────────────────
// La tarjeta del visor se quedaba congelada en "subiendo · 1 pendiente" si el
// usuario vaciaba la cola a mitad de sync. Todas las escrituras del status
// pasan por aquí:
//  - dedupe: solo se escribe si algo cambió (el poll llama cada ~1,5s vía
//    refreshPending; sin esto sería spam);
//  - heartbeat: aunque nada cambie, una escritura cada ≥60s para que
//    'ultimaSenal' no envejezca y la tarjeta no parezca muerta.
const STATUS_HEARTBEAT_MS = 60_000;
let lastStatusSnapshot = '';
let lastStatusAt = 0;

export function countQueuedPhotos(items: PendingDetail[]): number {
  let n = 0;
  for (const it of items) {
    const ph = it.detail.listNewImages;
    if (Array.isArray(ph)) n += ph.length;
  }
  return n;
}

export function pushQueueStatus(elementos: number, fotos: number, flushing: boolean): void {
  const estado = elementos === 0 ? 'al dia' : flushing ? 'subiendo' : 'pendiente';
  const cobertura = describeCoverage();
  const snapshot = `${elementos}|${fotos}|${estado}|${cobertura}`;
  const now = Date.now();
  if (snapshot === lastStatusSnapshot && now - lastStatusAt < STATUS_HEARTBEAT_MS) return;
  lastStatusSnapshot = snapshot;
  lastStatusAt = now;
  telemetryStatus({
    elementosPendientes: elementos,
    fotosPendientes: fotos,
    estado,
    cobertura,
  });
}

// Un evento por item y sesión al activarse el modo supervivencia (no por foto
// ni por pasada).
const survivalNotified = new Set<number>();

async function maybeAutoResetRetries(items: PendingDetail[]): Promise<void> {
  try {
    if (!items.some((i) => (i.retries ?? 0) > MAX_RETRIES)) return;
    const last = Number(localStorage.getItem(RETRY_RESET_KEY) || 0);
    if (Date.now() - last < AUTO_RETRY_RESET_MS) return;
    localStorage.setItem(RETRY_RESET_KEY, String(Date.now()));
    await resetAllPendingRetries();
    // Reflejar en memoria para que ESTA pasada ya los procese.
    items.forEach((i) => { i.retries = 0; });
    telemetryLog('reintentos_reseteados', { elementos: items.length });
  } catch { /* best-effort: si falla, se reintenta en la próxima pasada */ }
}

// Callback que el coordinador registra para refrescar work units conforme
// avanza el sync (cada foto subida cambia el total). Evita import circular
// con useOfflineSync — el caller hace setSyncProgressCallback(refreshPending).
let syncProgressCallback: (() => void) | null = null;
export function setSyncProgressCallback(cb: (() => void) | null): void {
  syncProgressCallback = cb;
}
function notifySyncProgress(): void {
  try { syncProgressCallback?.(); } catch { /* no bloquear el sync */ }
}

// Reconstruye un Blob a partir de los datos persistidos en IDB.
// Replica el flujo de EditDetailPage: prefiere fetch(dataURL) — más fiable y
// compatible con cualquier navegador — y cae a base64 como último recurso. El
// Blob final se reempaqueta con type 'image/jpg' porque el backend rechaza
// 'image/jpeg' en SaveMultiPartImages. Si la foto es pesada, intentamos
// comprimirla antes de devolverla para acelerar uploads en redes débiles.
async function photoToBlob(photo: ApiImage, survival = false): Promise<Blob | null> {
  let raw: Blob | null = null;
  if (photo.urlImage && (photo.urlImage.startsWith('data:') || photo.urlImage.startsWith('blob:'))) {
    try {
      const r = await fetch(photo.urlImage);
      const b = await r.blob();
      if (b.size > 0) raw = b;
    } catch { /* fallback a base64 */ }
  }
  if (!raw && photo.imageBase64) {
    try {
      const bytes = atob(photo.imageBase64);
      const arr = new Uint8Array(bytes.length);
      for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
      raw = new Blob([arr], { type: 'image/jpeg' });
    } catch { /* base64 corrupto */ }
  }
  if (!raw || raw.size === 0) return null;
  // Comprimir si la foto pesa demasiado para redes débiles. En modo
  // supervivencia (≥5 reintentos reales) la compresión es agresiva y SIEMPRE
  // se aplica — solo al envío: el blob original de IDB no se toca. La función
  // hace fallback a `raw` si algo falla.
  const compressed = await maybeCompressImage(
    raw,
    survival ? { maxDim: SURVIVAL_MAX_DIM, quality: SURVIVAL_QUALITY, force: true } : undefined,
  );
  // Reempaquetar con MIME image/jpg — backend lo exige así (espejo Android).
  return new Blob([await compressed.arrayBuffer()], { type: 'image/jpg' });
}

// Recompresión opcional para uploads encolados (offline sync). En red débil
// pasar de 4-5 MB a 1-2 MB triplica la probabilidad de no hacer timeout.
// Parámetros conservadores para preservar la calidad visual:
//   - Umbral 3 MB: solo tocamos fotos muy grandes; las normales pasan
//     intactas.
//   - Máx 2560px: mantiene detalle suficiente para leer matrículas,
//     contenedores, precintos. Una foto típica de móvil es 3000-4000px.
//   - Calidad 0.92: JPEG visualmente indistinguible del original en este
//     rango. No produce artefactos ni "grisáceo".
//   - Si la compresión no reduce tamaño o falla algo, devolvemos el blob
//     original sin tocar.
// La compresión solo se aplica en el flujo offline encolado — el upload online
// directo nunca pasa por aquí.
const COMPRESS_THRESHOLD_BYTES = 3 * 1024 * 1024;
const COMPRESS_MAX_DIM = 2560;
const COMPRESS_QUALITY = 0.92;

export async function maybeCompressImage(
  blob: Blob,
  opts?: { maxDim?: number; quality?: number; force?: boolean },
): Promise<Blob> {
  const maxDim = opts?.maxDim ?? COMPRESS_MAX_DIM;
  const quality = opts?.quality ?? COMPRESS_QUALITY;
  if (!opts?.force && blob.size < COMPRESS_THRESHOLD_BYTES) return blob;
  // createImageBitmap es más rápido y libera memoria automáticamente vs
  // <img>+canvas — disponible en Chromium y Safari modernos.
  try {
    const bitmap = await createImageBitmap(blob);
    const { width, height } = bitmap;
    const ratio = Math.min(maxDim / width, maxDim / height, 1);
    const w = Math.max(1, Math.round(width * ratio));
    const h = Math.max(1, Math.round(height * ratio));
    const canvas = typeof OffscreenCanvas !== 'undefined'
      ? new OffscreenCanvas(w, h)
      : Object.assign(document.createElement('canvas'), { width: w, height: h });
    const ctx = (canvas as HTMLCanvasElement | OffscreenCanvas).getContext('2d');
    if (!ctx) { bitmap.close?.(); return blob; }
    (ctx as CanvasRenderingContext2D).drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();
    const out = await new Promise<Blob | null>((resolve) => {
      if (canvas instanceof OffscreenCanvas) {
        canvas.convertToBlob({ type: 'image/jpeg', quality })
          .then((b) => resolve(b))
          .catch(() => resolve(null));
      } else {
        canvas.toBlob((b) => resolve(b), 'image/jpeg', quality);
      }
    });
    if (!out || out.size === 0 || out.size >= blob.size) return blob;
    return out;
  } catch {
    return blob;
  }
}

// Sube fotos una a una, eliminándolas de la cola tras cada éxito CONFIRMADO
// (espejo de uploadImagesOneByOne de Android: MainRepository.kt línea 100-138).
// Si una falla, rompe el bucle y retorna allDone=false para reintento.
//
// Garantías frente a red débil:
//  - El upload de la foto y la persistencia del estado están separados: si el
//    upload triunfa pero el persist falla (cuota IDB, condición de carrera al
//    cerrar la app), seguimos avanzando con `remaining` en memoria y al final
//    el caller verá allDone correcto. Esto evita reintentar fotos ya en
//    servidor y duplicarlas.
//  - Una foto con datos corruptos NO se descarta en silencio: entra en el
//    ciclo de descarte controlado.
async function uploadPhotosOneByOne(
  pendingId: number,
  detailId: number,
  photos: ApiImage[],
  survival = false,
): Promise<{ uploaded: number; failed: number; allDone: boolean; lostLease: boolean }> {
  let uploaded = 0;
  let failed = 0;
  const remaining = [...photos];

  const safePersist = async (resetBackoff = false) => {
    try {
      await persistRemainingPhotos(pendingId, remaining, resetBackoff);
    } catch (err) {
      // No bloqueante para el flujo, pero CRÍTICO de saber: si IDB falla aquí,
      // la próxima sync podría re-subir fotos ya en servidor y generar
      // duplicados visibles para el usuario.
      captureSyncError(err, {
        stage: 'persistRemainingPhotos',
        pendingId,
        remainingPhotos: remaining.length,
      });
    }
  };

  for (const photo of [...photos]) {
    // Item con muchas fotos = procesado largo: renovamos el lease por foto. Si
    // la renovación dice que YA NO somos dueños (nos congelaron >TTL y el otro
    // contexto robó el lease, o IDB no responde), abortamos el item EN EL
    // ACTO: ni esta foto, ni persists, ni ningún write más de este item —
    // ahora es del otro. Sin este corte, la carrera de duplicados volvería en
    // versión lenta.
    if (!(await renewPendingLease(pendingId, LEASE_OWNER))) {
      return { uploaded, failed, allDone: false, lostLease: true };
    }
    const blob = await photoToBlob(photo, survival);
    if (!blob) {
      // Blob irreconstruible (IDB corrupto, dataURL vaciado…). NO se descarta
      // en silencio: entra en el ciclo de descarte CONTROLADO. La pasada
      // continúa con la siguiente foto para no bloquear ni al resto del item
      // ni a la cola.
      if (!photo.unrecoverableAt) {
        photo.unrecoverableAt = Date.now();
        await safePersist();
        telemetryLog('foto_perdida', {
          item: pendingId,
          detalle: detailId,
          foto: photo.imageName ?? '(sin nombre)',
        });
        captureSyncError(new Error('Foto encolada irreconstruible (blob nulo)'), {
          stage: 'photoToBlob',
          pendingId,
          detailId,
          imageName: photo.imageName ?? '(sin nombre)',
        });
      }
      const now = Date.now();
      const lastAttempt = photo.unrecoverableLastCheckAt ?? 0;
      if (now - lastAttempt >= UNRECOVERABLE_CHECK_GAP_MS) {
        // Persistimos el intento ANTES del GET: el throttle vale también para
        // intentos fallidos — sin él, el poll (cada ~1,5s con cola)
        // martillearía el endpoint indefinidamente.
        photo.unrecoverableLastCheckAt = now;
        await safePersist();
        const names = await fetchServerPhotoNames(detailId);
        if (names != null) {
          if (names.has(normPhotoName(photo.imageName))) {
            // La foto SÍ consta en servidor (subida por otra vía o antes de
            // corromperse el blob) → limpieza SIN pérdida.
            const idx = remaining.indexOf(photo);
            if (idx >= 0) remaining.splice(idx, 1);
            await safePersist(true); // consta en servidor = progreso real
            uploaded++;
            telemetryLog('foto_subida', {
              detalle: detailId,
              foto: photo.imageName ?? '(sin nombre)',
              fotosRestantes: remaining.length,
              confirmadaPorLectura: true,
            });
            notifySyncProgress();
            continue;
          }
          // 200 REAL confirmando ausencia: cuenta. Un fallo de red jamás llega
          // aquí (names === null).
          photo.unrecoverableChecks = (photo.unrecoverableChecks ?? 0) + 1;
          await safePersist();
          const checks = photo.unrecoverableChecks;
          const ageMs = now - photo.unrecoverableAt;
          if (checks >= UNRECOVERABLE_MIN_CHECKS && ageMs >= UNRECOVERABLE_MIN_AGE_MS) {
            // Descarte definitivo: irreconstruible local + ausente en
            // servidor, confirmado N veces a lo largo de ≥48h.
            const idx = remaining.indexOf(photo);
            if (idx >= 0) remaining.splice(idx, 1);
            await safePersist();
            const horas = Math.round(ageMs / 3_600_000);
            telemetryLog('foto_descartada', {
              detalle: detailId,
              foto: photo.imageName ?? '(sin nombre)',
              comprobaciones: checks,
              horasDesdePrimerFallo: horas,
            });
            captureSyncError(new Error('Foto irreconstruible descartada tras comprobaciones confirmadas'), {
              stage: 'discardUnrecoverable',
              pendingId,
              detailId,
              imageName: photo.imageName ?? '(sin nombre)',
              comprobaciones: checks,
              horas,
            });
            // Aviso visible NO bloqueante (toast fijo, DiscardedPhotoNotice).
            // Jamás un alert(): congelaría el event loop en plena pasada, con
            // leases adquiridos que caducarían y el SW robaría. Sentry ya
            // lleva el registro permanente; esto es solo la señal al usuario.
            addDiscardNotice('offline.notice.photoDiscarded', {
              name: photo.imageName ?? '?',
              checks,
              hours: horas,
            });
            notifySyncProgress();
            continue;
          }
        }
        // names === null (GET caído): no cuenta jamás como ausencia.
      }
      failed++;
      continue;
    }
    // Autocuración: si una foto antes marcada como irreconstruible vuelve a
    // dar blob (IDB se recuperó), limpiamos marca Y contador — la historia de
    // comprobaciones de una foto sana no debe existir.
    if (photo.unrecoverableAt) {
      delete photo.unrecoverableAt;
      delete photo.unrecoverableChecks;
      delete photo.unrecoverableLastCheckAt;
      await safePersist();
    }
    try {
      const fd = new FormData();
      fd.append('photos', blob, photo.imageName || 'photo.jpg');
      await uploadImages(detailId, fd);
    } catch (err) {
      failed++;
      captureSyncError(err, {
        stage: 'uploadPhoto',
        pendingId,
        detailId,
        imageName: photo.imageName ?? '(sin nombre)',
        blobSizeKb: Math.round(blob.size / 1024),
        remainingPhotos: remaining.length,
      });
      // Igual que Android: rompemos al primer error para no agotar retries
      // contra un servidor que está fallando. El siguiente sync seguirá por
      // donde nos quedamos — las fotos previamente subidas ya están fuera de
      // `remaining` y persistidas.
      break;
    }
    // El 200 del POST no basta: confirmamos por LECTURA que la foto consta
    // antes de sacarla de la cola. GET caído/null = "no pude verificar" → la
    // foto SE QUEDA (jamás cuenta como ausente); si realmente entró, el
    // pre-filtro por fileName la limpiará en la próxima pasada sin re-subirla
    // (y si se re-sube, el backend deduplica por FileName — inocuo).
    const serverNames = await fetchServerPhotoNames(detailId);
    const confirmed = serverNames != null && serverNames.has(normPhotoName(photo.imageName));
    if (!confirmed) {
      failed++;
      captureSyncError(new Error('Subida sin confirmar por lectura'), {
        stage: 'verifyPhoto',
        pendingId,
        detailId,
        imageName: photo.imageName ?? '(sin nombre)',
        getFailed: serverNames == null,
      });
      // Paramos la pasada: o el GET no responde (red mal) o el POST no llegó a
      // registrarse (servidor raro) — en ambos casos insistir ahora no ayuda.
      break;
    }
    uploaded++;
    const idx = remaining.indexOf(photo);
    if (idx >= 0) remaining.splice(idx, 1);
    await safePersist(true); // foto confirmada = progreso real → backoff a cero
    telemetryLog('foto_subida', {
      detalle: detailId,
      foto: photo.imageName ?? '(sin nombre)',
      fotosRestantes: remaining.length,
    });
    // Refrescar work units para que el banner de progreso reaccione a cada
    // foto subida, no solo al fin del item.
    notifySyncProgress();
  }
  return { uploaded, failed, allDone: remaining.length === 0, lostLease: false };
}

// Actualiza listNewImages del item pendiente tras subir una foto.
// resetBackoff=true cuando el persist refleja PROGRESO REAL (foto confirmada
// en servidor): reinicia retries y nextAttemptAt para que el backoff
// exponencial no castigue a un item que está avanzando.
async function persistRemainingPhotos(
  pendingId: number,
  remaining: ApiImage[],
  resetBackoff = false,
): Promise<void> {
  const item = await getPendingById(pendingId);
  if (!item) return;
  item.detail.listNewImages = remaining;
  if (resetBackoff) {
    item.retries = 0;
    item.nextAttemptAt = undefined;
  }
  await putPendingItem(item);
}

function normPhotoName(name?: string): string {
  return (name ?? '').trim().toLowerCase();
}

// Fotos que CONSTAN en servidor para un detalle, como Set de nombres
// normalizados. La clave es el campo `fileName` de la respuesta (nombre
// original del multipart) — `imageName` en las respuestas es el GUID que
// genera el servidor y no casa jamás con el nombre local: el filtro histórico
// que comparaba por imageName era un no-op silencioso.
// null = "no pude leer" — el caller DEBE tratarlo como no-verificable
// (conservador), nunca como "no hay fotos".
async function fetchServerPhotoNames(detailId: number): Promise<Set<string> | null> {
  if (detailId <= 0) return null;
  try {
    // getImages ya lleva timeout generoso (30s) — este GET decide si una foto
    // sale de la cola, no puede ser impaciente.
    const r = await getImages(detailId);
    const data = r.data;
    const list: ApiImage[] = Array.isArray(data)
      ? data
      : (data as { listDataImage?: ApiImage[] })?.listDataImage ?? [];
    const names = new Set<string>();
    for (const p of list) {
      const n = normPhotoName(p.fileName);
      if (n) names.add(n);
    }
    return names;
  } catch {
    return null;
  }
}

// Antes de subir fotos en EDIT, eliminamos las que ya estén en servidor
// (espejo de removeAlreadyUploadedImages: MainRepository.kt línea 140-171).
// Evita re-subir cuando el usuario añadió fotos online, o cuando una subida
// previa quedó sin verificar (200 recibido pero GET de confirmación caído):
// esta pasada la limpia sin re-enviarla.
async function filterAlreadyUploaded(detailId: number, photos: ApiImage[]): Promise<ApiImage[]> {
  if (detailId <= 0 || photos.length === 0) return photos;
  const existing = await fetchServerPhotoNames(detailId);
  if (existing == null || existing.size === 0) return photos;
  return photos.filter((p) => !existing.has(normPhotoName(p.imageName)));
}

// ── Detalle destino borrado en el portal ────────────────────────────────────
// Confirma con una LECTURA real si el detalle ya NO existe en su cargo.
//   true  = el servidor respondió (200) y el detalle NO está en la lista →
//           borrado CONFIRMADO.
//   false = el detalle sigue estando, la respuesta no fue una lista, O no
//           pudimos leer (red caída) → NUNCA se confirma ausencia.
// Usa la lista COMPLETA del cargo (getDetailsByCargo con showLoaded=true por
// defecto): un detalle CARGADO no puede aparecer como ausente. Ante cualquier
// duda preferimos seguir reintentando a purgar por error un detalle real.
async function confirmDetailGone(idCargo: number, idCargoDetail: number): Promise<boolean> {
  if (idCargoDetail <= 0) return false;
  try {
    const r = await getDetailsByCargo(idCargo);
    const data = r.data;
    if (!Array.isArray(data)) return false;
    return !(data as Array<{ idCargoDetail?: number }>).some(
      (d) => Number(d?.idCargoDetail) === idCargoDetail,
    );
  } catch {
    return false;
  }
}

// Cierra el bucle cuando el destino está CONFIRMADO inexistente (solo se llama
// tras confirmDetailGone === true). Regla anti-pérdida de fotos:
//   - SIN fotos pendientes → purgar el item (return 'synced' → el loop lo
//     borra) y quitar el detalle fantasma de la caché offline.
//   - CON fotos pendientes → JAMÁS descartar en silencio: marcar el item
//     (targetGoneAt) para que deje de reintentarse pero siga VISIBLE con las
//     fotos conservadas. El aviso al usuario lo emite el coordinador
//     (useOfflineSync) al ver la marca — una sola vez por sesión, cubriendo
//     también items marcados por el SW en background.
async function handleTargetDetailGone(item: PendingDetail, editId: number): Promise<ProcessOutcome> {
  const fresh = (await getPendingById(item.id)) ?? item;
  const photoCount = Array.isArray(fresh.detail.listNewImages) ? fresh.detail.listNewImages.length : 0;
  if (photoCount === 0) {
    telemetryLog('detalle_no_existe', { idCargoDetail: editId, fotos: 0 });
    await updateCachedCargoDetails(item.idCargo, (list) =>
      list.filter((d) => d.idCargoDetail !== editId)
    ).catch(() => { /* mejor esfuerzo */ });
    invalidateSwApiCache({ idCargo: item.idCargo, idCargoDetail: editId });
    return 'synced';
  }
  if (fresh.targetGoneAt == null) {
    await putPendingItem({ ...fresh, targetGoneAt: Date.now() });
  }
  return 'skipped';
}

// Resultado del procesado de un item:
//  - 'synced'    → completado; el caller lo borra de la cola.
//  - 'dataSent'  → (solo pasada 'data') los DATOS llegaron al servidor;
//                  quedan fotos para la pasada 2. Ni bump ni borrado — solo
//                  release del lease.
//  - 'retry'     → falló; bump de retries + release del lease.
//  - 'lostLease' → otro contexto nos robó el lease a mitad; el caller NO debe
//                  escribir NADA de este item (ni bump ni release) — ya no es
//                  nuestro.
//  - 'skipped'   → aparcado (back-off de 404, destino inexistente); ni bump ni
//                  fallo — solo release del lease.
//  - 'aborted'   → lo cancelamos NOSOTROS (el controlador pulsó "Trabajar
//                  offline" a mitad de la subida y el interceptor abortó lo que
//                  había en vuelo). No es un fallo: ni bump, ni fallo contado,
//                  ni reporte a Sentry — solo release del lease. Sin esto, una
//                  pulsación del botón castigaba con retry + backoff exponencial
//                  a TODOS los items en vuelo (visto en campo: 15 de golpe).
type ProcessOutcome = 'synced' | 'dataSent' | 'retry' | 'lostLease' | 'skipped' | 'aborted';

// Procesa UN item pendiente. No lanza.
//
// `mode` implementa el orden DATOS-PRIMERO (patrón Android): en la pasada
// 'data' viajan solo los payloads JSON (KBs) de TODOS los items; las fotos
// pesadas esperan a la pasada 'photos'. Así, con cobertura mínima, los datos
// de todas las cargas llegan al portal aunque ninguna foto lo consiga.
async function processPendingItem(
  item: PendingDetail,
  ctx: SyncContext,
  mode: 'data' | 'photos',
): Promise<ProcessOutcome> {
  if (ctx.signal?.aborted) return 'aborted';

  // Los items NUNCA se borran solos por antigüedad: una carga con fotos de
  // hace 8 días sigue siendo trabajo del controller que debe llegar al portal.
  // (El descarte a los 7 días era un vector de pérdida silenciosa — eliminado;
  // la app Android tampoco descarta, solo avisa.)
  // Pasado el tope de reintentos, se aparca hasta el próximo auto-reset (cada
  // 30 min en flushQueueFromClient) — nunca se abandona de forma permanente.
  if ((item.retries ?? 0) > MAX_RETRIES) {
    return 'retry'; // dejar pero no procesar en esta pasada
  }

  // Destino confirmado inexistente (posiblemente marcado por el SW entre el
  // snapshot del bucle y el lease): no reintentar, mantener visible. El aviso
  // lo da el coordinador al ver la marca.
  if (item.targetGoneAt != null) return 'skipped';

  // Back-off de 404: con ≥3 seguidos, un intento cada 15 min como máximo.
  if (
    (item.notFound404s ?? 0) >= NOT_FOUND_BACKOFF_AFTER &&
    item.lastAttemptAt != null &&
    Date.now() - item.lastAttemptAt < NOT_FOUND_BACKOFF_MS
  ) {
    return 'skipped';
  }

  // Sacamos las fotos del payload del API (van por multipart aparte).
  const { listNewImages, ...detailRest } = item.detail;
  const photos: ApiImage[] = (listNewImages ?? []) as ApiImage[];
  // La metadata encolada ya trae su EditTs (se construyó al guardar). Para
  // items de antes lo inyectamos con createdAt — el instante del guardado —
  // SIN regenerar nunca uno existente: el reintento no es una edición nueva.
  const detailPayload = {
    ...detailRest,
    metadata: ensureEditTs(detailRest.metadata as string | undefined, item.createdAt),
  } as Partial<Detail>;

  // Para los ESPEJOS de caché local, sin listDeleteImages: es un COMANDO que
  // viaja y se aplica en el POST, no un dato del detalle. Cachearlo haría que
  // la edición inline (que envía {...current}) re-enviara borrados viejos —
  // no-op en el backend, pero ruido que confunde.
  const { listDeleteImages: _appliedDeletes, ...cacheRest } = detailRest;
  void _appliedDeletes;

  try {
    if (item.operateType === 'NEW') {
      // Un NEW solo se procesa en la pasada de datos (sin id de servidor no
      // hay dónde subir fotos). La pasada 2 lo verá ya como EDIT.
      if (mode === 'photos') return 'retry';
      // Barrera anti-duplicado por re-entrega: CreateCargoDetail inserta a
      // CIEGAS. Si una petición llega al servidor pero la respuesta se pierde
      // —45s de timeout, algo normal con cobertura mala—, el item sigue
      // marcado como NEW y el siguiente intento crearía un SEGUNDO detalle
      // idéntico. Ocurrió de verdad en las cargas 28211 y 28212 de agosto de
      // 2026. Se hace aquí y no en el servidor a propósito: ese endpoint lo
      // comparten la app Android y el portal, y en el flujo clásico SÍ es
      // legítimo repetir contenedor en una misma carga. GET fallido = no
      // verificable → seguimos con el create (comportamiento previo).
      let newId = await findMatchingServerDetailId(item.idCargo, item.detail.containerNumber);
      if (!newId) {
        const res = await createDetail(item.idCargo, await conIdsMaestros(item.idCargo, detailPayload));
        newId = (res.data as { idCargoDetail?: number })?.idCargoDetail ?? 0;
      }
      if (!newId) return 'retry';
      // Promovemos a EDIT antes de subir fotos: si el upload falla, el
      // siguiente sync hará EDIT en vez de duplicar el detalle. (El promote
      // deja también dataSyncedAt — los datos ya viajaron.)
      await updatePendingDetailToEdit(item.id, newId);
      if (photos.length > 0) {
        // Datos-primero: las fotos de este item esperan a la pasada 2, detrás
        // de los datos de TODAS las demás cargas.
        return 'dataSent';
      }
      // Espejar en caché para que la vista offline incluya el nuevo detalle
      // sin esperar al siguiente fetch online.
      await updateCachedCargoDetails(item.idCargo, (list) => {
        if (list.some((d) => d.idCargoDetail === newId)) return list;
        return [...list, { ...cacheRest, idCargoDetail: newId, idCargo: item.idCargo } as Detail];
      }).catch(() => { /* mejor esfuerzo */ });
      invalidateSwApiCache({ idCargo: item.idCargo, idCargoDetail: newId });
      return 'synced';
    }

    if (item.operateType === 'EDIT') {
      const editId = item.idCargoDetail || item.detail.idCargoDetail || 0;
      if (editId <= 0) return 'retry';
      if (mode === 'data') {
        // dataSyncedAt: los datos ya viajaron en una pasada anterior y el
        // usuario no los ha vuelto a tocar — no re-POSTeamos en cada tick.
        if (!item.dataSyncedAt) {
          const editRes = await editDetail(editId, detailPayload);
          // Defensivo: un 2xx sin el Detail esperado en el cuerpo = FALLO.
          const editBody = editRes.data as { idCargoDetail?: number } | null | undefined;
          if (!editBody || typeof editBody !== 'object' || !(Number(editBody.idCargoDetail) > 0)) {
            return 'retry';
          }
          // dataSyncedAt: datos entregados. notFound404s: un EDIT aceptado
          // rompe cualquier racha de 404 (el detalle existe).
          // listDeleteImages: los borrados YA se aplicaron en este POST —
          // fuera del item, para que una edición posterior del pending (que
          // limpia dataSyncedAt) no re-envíe borrados viejos.
          await putPendingItem({
            ...item,
            detail: { ...item.detail, listDeleteImages: undefined },
            dataSyncedAt: Date.now(),
            notFound404s: undefined,
            serverRejects: undefined,
            serverRejectMsg: undefined,
          });
        }
        if (photos.length > 0) return 'dataSent';
        // Sin fotos: los datos eran todo el trabajo — cerrar ya.
      }
      if (mode === 'photos' && photos.length > 0) {
        // Modo supervivencia: con ≥5 reintentos REALES del item (los fallos
        // sin red no cuentan), recompresión agresiva del envío.
        const survival = (item.retries ?? 0) >= SURVIVAL_RETRIES;
        if (survival && !survivalNotified.has(item.id)) {
          survivalNotified.add(item.id);
          telemetryLog('modo_supervivencia', {
            item: item.id,
            reintentos: item.retries ?? 0,
            fotos: photos.length,
          });
        }
        const filtered = await filterAlreadyUploaded(editId, photos);
        if (filtered.length === 0) {
          // Todas las fotos ya están en servidor; limpiamos (progreso real).
          await persistRemainingPhotos(item.id, [], true);
        } else {
          const result = await uploadPhotosOneByOne(item.id, editId, filtered, survival);
          if (result.lostLease) return 'lostLease';
          if (!result.allDone) {
            // La subida FALLÓ. Solo AHORA (no en el camino de éxito, que no
            // paga latencia extra) comprobamos si el detalle destino sigue
            // existiendo: si lo borraron en el portal, las fotos nunca
            // entrarán y el item quedaría en bucle. Confirmado el borrado →
            // marcar + conservar fotos + avisar; si no (red/existe) → retry.
            if (await confirmDetailGone(item.idCargo, editId)) {
              return handleTargetDetailGone(item, editId);
            }
            return 'retry';
          }
        }
      }
      // Espejar la edición en caché.
      await updateCachedCargoDetails(item.idCargo, (list) =>
        list.map((d) =>
          d.idCargoDetail === editId ? { ...d, ...cacheRest, idCargoDetail: editId } as Detail : d
        )
      ).catch(() => { /* mejor esfuerzo */ });
      invalidateSwApiCache({ idCargo: item.idCargo, idCargoDetail: editId });
      return 'synced';
    }

    if (item.operateType === 'DELETE') {
      // Aceptamos solo ids enteros positivos. Items con id<=0 vienen de un NEW
      // pendiente cancelado (que ya no tiene sentido en servidor) o de una
      // entry corrupta de versiones previas. En ambos casos el item debe salir
      // de la cola sin tocar el backend (atascarse llamando a /undefined o /0
      // generaría 404 indefinidos).
      const delId = Number(item.idCargoDetail || item.detail?.idCargoDetail || 0);
      if (!Number.isFinite(delId) || delId <= 0) return 'synced';
      try {
        await deleteDetail(delId);
      } catch (err) {
        // 404 = el detalle ya no existe en el portal → borrado IDEMPOTENTE:
        // borrar algo ya borrado es objetivo cumplido, no un fallo (paridad
        // Android). Se trata igual que un 200 (purga + limpia caché). Solo el
        // 404 (respuesta REAL del servidor) cuenta; red/5xx/timeout se relanzan
        // al catch genérico y reintentan como hasta ahora.
        const st = (err as { response?: { status?: number } })?.response?.status;
        if (st !== 404) throw err;
        telemetryLog('delete_ya_no_existe', { idCargoDetail: delId });
      }
      // Quitar también de la caché local.
      await updateCachedCargoDetails(item.idCargo, (list) =>
        list.filter((d) => d.idCargoDetail !== delId)
      ).catch(() => { /* mejor esfuerzo */ });
      invalidateSwApiCache({ idCargo: item.idCargo, idCargoDetail: delId });
      return 'synced';
    }
  } catch (err) {
    // Cancelación nuestra (el usuario pulsó "Trabajar offline" a mitad): no ha
    // fallado nada, solo se ha pospuesto. Sale ANTES del 404 y de Sentry — un
    // abort no trae response ni merece un evento de error.
    if (esCancelacionPropia(err)) return 'aborted';
    // 404 en EDIT (detalle borrado en portal): CONFIRMAMOS con lectura real
    // antes de decidir. Confirmado → cerrar el bucle (purga sin fotos /
    // marca+conserva con fotos). No confirmado (red caída o el detalle sigue)
    // → back-off de 404 como hasta ahora + se reporta.
    const httpStatus = (err as { response?: { status?: number } })?.response?.status;
    if (httpStatus === 404 && item.operateType === 'EDIT') {
      const editId = item.idCargoDetail || item.detail.idCargoDetail || 0;
      if (await confirmDetailGone(item.idCargo, editId)) {
        return handleTargetDetailGone(item, editId);
      }
      await putPendingItem({ ...item, notFound404s: (item.notFound404s ?? 0) + 1 }).catch(() => {});
    }
    // Rechazo REAL del servidor en la pasada de datos: se cuenta en el item
    // para que la UI pueda avisar al controlador. Cuentan los 5xx y los 4xx de
    // payload (400/413/415/422). NO cuentan: 401/403 (sesión, lo gestiona el
    // interceptor), 404 (camino propio arriba), 408/429 (transitorios), ni
    // nada sin respuesta (red, timeout, cancelación).
    if (mode === 'data' && esRechazoDelServidor(httpStatus)) {
      const http = describeHttpResponse(err);
      await putPendingItem({
        ...item,
        serverRejects: (item.serverRejects ?? 0) + 1,
        serverRejectMsg: `${httpStatus}${typeof http.httpBody === 'string' ? ` ${http.httpBody.slice(0, 160)}` : ''}`,
      }).catch(() => {});
    }
    // Reportamos a Sentry CADA fallo real de sync para tener visibilidad total
    // de qué impide que un pending llegue al servidor (red caída, 4xx por
    // payload inválido, 5xx, timeout, etc.).
    captureSyncError(err, {
      stage: 'processPendingItem',
      pendingId: item.id,
      idCargo: item.idCargo,
      idCargoDetail: item.idCargoDetail,
      operateType: item.operateType,
      retries: item.retries ?? 0,
      photoCount: Array.isArray(item.detail.listNewImages) ? item.detail.listNewImages.length : 0,
      // Resumen de lo que VIAJÓ (sin fotos): permite ver desde el visor qué
      // campo rompe un 4xx/5xx sin tener el móvil delante.
      payload: summarizePayload(detailPayload),
    });
    return 'retry';
  }
  return 'retry';
}

// Un NEW encolado sin ids maestros (la pantalla se abrio sin la Position) es
// un 500 seguro en el servidor: FK_CargoDetail_Product. Antes de enviarlo se
// completan desde la Position cacheada de esa carga, si la hay. Si no, va tal
// cual: desde sep 2026 el servidor tambien los completa desde la carga.
type IdsMaestros = { idProduct?: number; idOtherProduct?: number; idCarrier?: number; idPlanning?: number };
async function conIdsMaestros(idCargo: number, payload: Partial<Detail>): Promise<Partial<Detail> & IdsMaestros> {
  const actual = payload as Partial<Detail> & IdsMaestros;
  if (Number(actual.idProduct) > 0) return actual;
  try {
    const pos = await findCachedPositionByCargo(idCargo);
    if (!pos) return actual;
    telemetryLog('ids_maestros_completados', { idCargo, contenedor: payload.containerNumber ?? '' });
    return {
      ...actual,
      idProduct: pos.idProduct,
      idOtherProduct: pos.idOtherProduct,
      idCarrier: pos.idCarrier,
      idPlanning: pos.idPlanning,
    };
  } catch {
    return actual;
  }
}

// ¿Es un status HTTP que significa "el servidor recibió la petición y la
// rechazó por su contenido o por un fallo suyo"? Ver el uso en el catch de
// processPendingItem.
function esRechazoDelServidor(status: number | undefined): boolean {
  if (status == null) return false;
  if (status >= 500) return true;
  return status === 400 || status === 413 || status === 415 || status === 422;
}

// Resumen compacto del payload de un create/edit para telemetría: ids
// maestros y contenedor tal cual, strings largos por su longitud, números tal
// cual. Nunca incluye base64 ni listas de imágenes. Ocupa una línea.
function summarizePayload(payload: Partial<Detail>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(payload)) {
    if (v == null || v === '') continue;
    if (Array.isArray(v)) { parts.push(`${k}[${v.length}]`); continue; }
    if (typeof v === 'string') {
      parts.push(k === 'containerNumber' || v.length <= 12 ? `${k}=${v}` : `${k}(len ${v.length})`);
      continue;
    }
    if (typeof v === 'number' || typeof v === 'boolean') { parts.push(`${k}=${v}`); continue; }
    parts.push(`${k}:${typeof v}`);
  }
  return parts.join(' ');
}

// ¿Puede la pasada actual tocar este item? Espejo exacto de los dos saltos
// "sin red" del bucle de runPass (destino inexistente y backoff por item).
// El tercero (lease de otro contexto) no se puede saber sin adquirirlo.
function isEligibleNow(item: PendingDetail, now: number = Date.now()): boolean {
  if (item.targetGoneAt != null) return false;
  if (item.nextAttemptAt != null && now < item.nextAttemptAt) return false;
  return true;
}

// Coste de procesar un pending: prioridad 0 = más ligero.
// Diseñado para que el usuario vea progreso rápido en redes débiles.
function costOf(item: PendingDetail): number {
  if (item.operateType === 'DELETE') return 0;
  const photos = (item.detail.listNewImages ?? []) as ApiImage[];
  if (photos.length === 0) return 1;
  return 10 + photos.length;
}

// Busca en el cargo un detalle YA existente equivalente al NEW encolado (mismo
// containerNumber, la clave natural — espejo de findMatchingServerDetail en
// Android). 0 = sin match o GET fallido; containerNumber vacío no puede
// matchear.
async function findMatchingServerDetailId(
  idCargo: number,
  containerNumber?: string,
): Promise<number> {
  const cn = (containerNumber ?? '').trim().toUpperCase();
  if (!cn) return 0;
  try {
    const r = await getDetailsByCargo(idCargo);
    const data = r.data;
    const list: Array<{ idCargoDetail?: number; containerNumber?: string }> =
      Array.isArray(data) ? data : [];
    const matches = list.filter(
      (d) =>
        (d.containerNumber ?? '').trim().toUpperCase() === cn &&
        Number(d.idCargoDetail) > 0,
    );
    // Solo con match ÚNICO promovemos: con varios candidatos es ambiguo y
    // promover al equivocado sobrescribiría sus datos. Preferimos el residual
    // de duplicado al machaque — un duplicado se limpia, unos datos machacados
    // no se recuperan.
    if (matches.length === 1) return Number(matches[0].idCargoDetail);
    if (matches.length > 1) {
      telemetryLog('precreate_match_ambiguo', {
        cargo: idCargo,
        contenedor: cn,
        candidatos: matches.length,
      });
    }
    return 0;
  } catch {
    return 0;
  }
}

async function bumpRetryClient(id: number): Promise<void> {
  const item = await getPendingById(id);
  if (!item) return;
  item.retries = (item.retries ?? 0) + 1;
  item.lastAttemptAt = Date.now();
  // Backoff exponencial: el item queda en pausa hasta nextAttemptAt.
  item.nextAttemptAt = Date.now() + backoffDelayMs(item.retries);
  await putPendingItem(item);
  // Al CRUZAR el tope (una sola vez): el visor puede explicar por qué "no sube
  // nada" en vez de descubrirlo por el silencio.
  if (item.retries === MAX_RETRIES + 1) {
    telemetryLog('item_aparcado', {
      item: id,
      reintentos: item.retries,
      motivo: 'tope_reintentos',
    });
  }
}

// Punto de entrada: procesa todos los pendientes en dos pasadas.
// Mutex para que múltiples disparos no arranquen sincronizaciones concurrentes.
// Devuelve estadísticas del run (o null si offline / ya hay otro en marcha —
// los callers normales reusan el inFlight).
export async function flushQueueFromClient(ctx: SyncContext = {}): Promise<FlushStats | null> {
  // Si no hay red O el usuario está en modo offline manual, salimos sin
  // intentar nada. Sin esto, en modo manual las requests serían abortadas por
  // el interceptor → bumpRetry → consumiríamos el límite de reintentos sin
  // necesidad.
  if (isOfflineForUser()) return null;
  if (inFlight) return inFlight;
  inFlight = (async (): Promise<FlushStats> => {
    const startedAt = Date.now();
    let synced = 0;
    let failed = 0;
    let processedAny = false;
    // Items que la pasada se saltó sin tocarlos (destino inexistente, backoff
    // por item, lease de otro contexto). Se cuenta por SALTO, no por item: uno
    // que se salta en las dos pasadas suma 2. Solo alimenta la telemetría.
    let skipped = 0;
    // ¿Se llegó a anunciar 'sync_iniciado'? Marca la obligación de cerrar con
    // 'sync_terminado' aunque no se procesara nada — si no, en el visor la
    // pasada queda abierta para siempre y parece un cuelgue.
    let announced = false;
    try {
      const items = await getPendingDetails();
      if (items.length > 0) {
        // Auto-reset de reintentos cada 30 min: los items que agotaron su tope
        // vuelven a intentarse solos, sin que nadie pulse "forzar subida".
        // Va ANTES de decidir si anunciamos: el reset limpia nextAttemptAt y
        // convierte en elegibles items que un instante antes no lo eran.
        await maybeAutoResetRetries(items);
        // Solo se anuncia la pasada si hay algo que esta pasada PUEDE tocar.
        // Con la cola ocupada el coordinador llama cada ~3 s (POLL_BUSY) y, si
        // el único item está en backoff, cada tick escribía sync_iniciado +
        // fase_datos + sync_terminado en la RTDB sin haber hecho nada: miles de
        // eventos por hora que en el visor se leen como "reintenta sin parar"
        // (4 sep 2026, controlador de Egipto con un NEW que devolvía 500) y que
        // desplazan los eventos útiles fuera del limitToLast del visor. Los
        // saltos por lease ajeno no se pueden predecir sin adquirir el lease:
        // esos sí se anuncian, pero son la excepción, no el tick a tick.
        if (items.some((it) => isEligibleNow(it))) {
          telemetryLog('sync_iniciado', { elementosPendientes: items.length });
          announced = true;
          pushQueueStatus(items.length, countQueuedPhotos(items), true);
        }
      }
      // Orden por coste estimado ascendente (DELETEs y EDITs sin fotos
      // primero); tiebreaker por orden de inserción (id asc) — respeta
      // dependencias entre operaciones del mismo detalle.
      items.sort((a, b) => costOf(a) - costOf(b) || a.id - b.id);

      const runPass = async (list: PendingDetail[], mode: 'data' | 'photos') => {
        for (const item of list) {
          if (ctx.signal?.aborted) break;
          if (!navigator.onLine) break;
          // El controlador pulsó "Trabajar offline" a mitad de la pasada: su
          // decisión manda AHORA. Seguir solo serviría para que el interceptor
          // abortase item tras item, y cada aborto gastaba un reintento.
          if (isUserOfflineMode()) break;
          // Destino confirmado inexistente: no se reintenta nunca más (solo
          // "Forzar subida" lo reanima). Se salta a nivel de bucle — sin
          // lease, sin red. Sigue VISIBLE en la cola; el aviso lo gestiona
          // refreshPending.
          if (item.targetGoneAt != null) { skipped++; continue; }
          // Backoff por item: si su nextAttemptAt no ha llegado, se salta SIN
          // adquirir lease ni consumir retry. (Snapshot posiblemente viejo:
          // como mucho salta una pasada de más — inocuo.)
          if (item.nextAttemptAt != null && Date.now() < item.nextAttemptAt) { skipped++; continue; }
          // Lease cross-contexto: si el SW está procesando este item AHORA, lo
          // saltamos sin consumir retry. El acquire devuelve el item fresco
          // (el snapshot puede estar viejo si el SW avanzó).
          // try/catch: un freeze de pestaña (usuario saliendo a la cámara) a
          // mitad de la transacción get+put del acquire lanza
          // TransactionInactiveError — sin esto abortaba la PASADA entera (el
          // catch general está fuera del bucle) y dejaba a los items
          // siguientes sin procesar hasta el próximo tick. Tratarlo como "no
          // adquirido" = mismo camino que un lease ajeno.
          let fresh: PendingDetail | null;
          try {
            fresh = await acquirePendingLease(item.id, LEASE_OWNER);
          } catch {
            skipped++;
            continue;
          }
          if (!fresh) { skipped++; continue; }
          processedAny = true;
          const outcome = await processPendingItem(fresh, ctx, mode);
          if (outcome === 'synced') {
            await deletePendingDetail(item.id);
            synced++;
          } else if (outcome === 'dataSent') {
            // Datos entregados = éxito REAL de este intento: el backoff se
            // reinicia (castigar exponencialmente a un item que avanza solo
            // alargaría el drenaje).
            const freshAfter = await getPendingById(item.id);
            if (freshAfter && ((freshAfter.retries ?? 0) > 0 || freshAfter.nextAttemptAt != null)) {
              await putPendingItem({ ...freshAfter, retries: 0, nextAttemptAt: undefined });
            }
            // Liberamos el lease — si morimos antes de la pasada 2, el SW
            // puede continuar el item sin esperar el TTL.
            await releasePendingLease(item.id, LEASE_OWNER);
          } else if (outcome === 'retry') {
            // El retry solo cuenta si hubo conectividad real durante el intento
            // y el usuario no acaba de irse a offline manual — si se fue, lo que
            // vemos son nuestros propios abortos (las fotos no lanzan: devuelven
            // failed, así que aquí es donde se filtran). El lease se libera igual.
            if (navigator.onLine && !isUserOfflineMode()) await bumpRetryClient(item.id);
            await releasePendingLease(item.id, LEASE_OWNER);
            failed++;
          } else if (outcome === 'aborted') {
            // Ni bump ni fallo: no ha fallado nada, se ha pospuesto. Y cortamos
            // la pasada — lo que canceló este item cancelará los siguientes.
            await releasePendingLease(item.id, LEASE_OWNER);
            telemetryLog('item_procesado', {
              item: item.id,
              pasada: mode === 'data' ? 'datos' : 'fotos',
              resultado: 'CANCELADO',
            });
            notifySyncProgress();
            break;
          } else if (outcome === 'skipped') {
            // Aparcado (back-off de 404 / destino inexistente): ni bump ni
            // fallo, solo devolver el lease.
            await releasePendingLease(item.id, LEASE_OWNER);
          }
          // 'lostLease': el item ya es del otro contexto — cero writes.
          telemetryLog('item_procesado', {
            item: item.id,
            pasada: mode === 'data' ? 'datos' : 'fotos',
            resultado:
              outcome === 'synced' ? 'COMPLETED'
              : outcome === 'dataSent' ? 'DATOS_ENVIADOS'
              : outcome === 'retry' ? 'RETRY_LATER'
              : outcome === 'skipped' ? 'BACKOFF_404'
              // 'aborted' no llega aquí: su rama corta la pasada con break.
              : 'LEASE_PERDIDO',
          });
          // Refrescar el contador entre items, no solo al final del batch.
          notifySyncProgress();
        }
      };

      // ── PASADA 1 (datos-primero): los payloads JSON de TODAS las cargas
      // (KBs) llegan al portal antes de que la primera foto pesada acapare la
      // poca cobertura disponible.
      if (announced) telemetryLog('fase_datos', { elementos: items.length });
      await runPass(items, 'data');

      // ── PASADA 2: fotos. Releemos la cola — la pasada 1 promovió NEWs a
      // EDIT y pudo cerrar/fusionar items.
      const withPhotos = (await getPendingDetails())
        .filter((it) =>
          it.operateType === 'EDIT' &&
          (it.idCargoDetail || it.detail.idCargoDetail || 0) > 0 &&
          Array.isArray(it.detail.listNewImages) &&
          it.detail.listNewImages.length > 0)
        .sort((a, b) => costOf(a) - costOf(b) || a.id - b.id);
      if (announced && withPhotos.length > 0) telemetryLog('fase_fotos', { elementos: withPhotos.length });
      await runPass(withPhotos, 'photos');
    } catch (err) {
      // Errores fuera del catch de processPendingItem — típicamente fallos de
      // IDB al leer la cola o al borrar/actualizar pendings. También los
      // reportamos para no perder visibilidad.
      captureSyncError(err, { stage: 'flushQueueFromClient' });
    } finally {
      inFlight = null;
      // Analytics solo si llegamos a procesar algo: evita ruido cuando el
      // coordinador llama tryFlush en un poll donde no había nada pendiente.
      if (processedAny) {
        track({
          name: 'offline_queue_synced',
          params: {
            synced_count: synced,
            failed_count: failed,
            duration_ms: Date.now() - startedAt,
          },
        });
      }
      // La telemetría en vivo cierra SIEMPRE que se haya anunciado el inicio,
      // aunque todo se saltara (backoff, lease ajeno, destino inexistente).
      // El visor empareja inicio/fin: sin este cierre la pasada se queda
      // abierta y se lee como un sync colgado cuando en realidad no había
      // nada que hacer.
      if (announced) {
        telemetryLog('sync_terminado', {
          subidos: synced,
          fallidos: failed,
          saltados: skipped,
          ms: Date.now() - startedAt,
        });
      }
      // …pero el STATUS final se escribe SIEMPRE: 'subiendo' jamás puede
      // quedarse como estado terminal. Cubre pasadas donde todo se saltó
      // (lease ajeno, backoff), aborts, y el caso "el usuario vació la cola a
      // mitad del sync". La puerta única dedupea, así que repetirlo es barato.
      try {
        const remaining = await getPendingDetails();
        pushQueueStatus(remaining.length, countQueuedPhotos(remaining), false);
      } catch { /* best-effort */ }
    }
    return { synced, failed, processedAny };
  })();
  return inFlight;
}

// Resetea contadores de retry para items abandonados — botón "forzar subida".
export async function resetAllRetriesClient(): Promise<void> {
  await resetAllPendingRetries();
}

// "Forzar subida" necesita un flush FRESCO: flushQueueFromClient devuelve la
// promesa del flush EN CURSO si lo hay — y con cola pendiente el poll busy casi
// garantiza que lo hay. Unirse a esa pasada (que arrancó ANTES del reset de
// retries y ya decidió saltarse los aparcados) convertía el botón en un no-op
// justo cuando más falta hacía. Aquí esperamos a que termine la pasada vieja y
// lanzamos una nueva que SÍ ve el reset.
export async function flushQueueFreshFromClient(): Promise<FlushStats | null> {
  if (inFlight) {
    try { await inFlight; } catch { /* la pasada vieja no nos importa */ }
  }
  return flushQueueFromClient();
}

// Borra el throttle del auto-reset de 30 min: tras un "Forzar subida" el
// próximo ciclo automático debe poder resetear de nuevo sin esperar.
export function clearAutoRetryThrottle(): void {
  try { localStorage.removeItem(RETRY_RESET_KEY); } catch { /* sin storage */ }
}
